import { IonIcon } from '@ionic/react';
import clsx from 'clsx';
import { trendingDown, trendingUp } from 'ionicons/icons';
import React from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router';

import PercentChange from './PercentChange';

const COLOR_RED = '#BF4858';
const COLOR_GREEN = '#3E9D8F';
const COLOR_RED_DARK = '#F44336';
const COLOR_GREEN_DARK = '#4CAF50';

const useStyles = createUseStyles({
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  fadeIn: {
    opacity: 0,
    animation: '$fadeIn 0.5s ease-in-out forwards',
  },
  hidden: {
    opacity: 0,
  },
  row: {
    display: 'flex',
    gap: 'clamp(2px, calc(2px + 3vw), 20px)',
  },
  module: {
    flex: '1 1 50%',
    minWidth: 0, // Prevents flex items from overflowing
  },
  card: {
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.04)',
    marginTop: '20px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
    padding: '15px',
    minWidth: '150px',
    backgroundColor: 'white',
    transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#1a1a1a',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.3)',
    },
  },
  icon: {
    marginLeft: '5px',
    marginRight: '3px',
    width: '18px',
    height: '18px',
    alignSelf: 'center',
    color: (props: PerformanceMetricStyleProps) => (props.isPositive ? COLOR_GREEN : COLOR_RED),
    '--ionicon-stroke-width': '50px',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: (props: PerformanceMetricStyleProps) =>
        props.isPositive ? COLOR_GREEN_DARK : COLOR_RED_DARK,
    },
  },
  primaryText: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#282828',
    margin: 0,
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#ffffff',
    },
  },
  secondaryText: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#666666',
    margin: 0,
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
  clickable: {
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease, transform 0.1s ease',
    '&:hover': {
      boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0px)',
    },

    // Dark mode hover effects
    '@media (prefers-color-scheme: dark)': {
      '&:hover': {
        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.5)',
        transform: 'translateY(-2px)',
      },
    },
  },
  percentText: {
    fontWeight: 600,
    color: (props: PerformanceMetricStyleProps) => (props.isPositive ? COLOR_GREEN : COLOR_RED),
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: (props: PerformanceMetricStyleProps) =>
        props.isPositive ? COLOR_GREEN_DARK : COLOR_RED_DARK,
    },
  },
});

interface PerformanceMetric {
  value: number;
  label: string;
  percentChange?: number;
}

interface PerformanceMetricStyleProps {
  isPositive: boolean;
}

interface PerformanceMetricsProps {
  data: {
    residentSubmissions: PerformanceMetric;
    completedWorkOrders: PerformanceMetric;
    deniedWorkOrders: PerformanceMetric;
    canceledWorkOrders: PerformanceMetric;
    residentsEngaged: PerformanceMetric;
  };
  isLoading: boolean;
}

const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({
  data: {
    residentSubmissions,
    completedWorkOrders,
    deniedWorkOrders,
    canceledWorkOrders,
    residentsEngaged,
  },
  isLoading,
}) => {
  const isPositive = (residentsEngaged.percentChange ?? 0) >= 0 ? true : false;
  const classes = useStyles({ isPositive });

  const history = useHistory();

  const contentClass = isLoading ? classes.hidden : classes.fadeIn;

  return (
    <>
      <div className={classes.row}>
        {/* Resident submissions */}
        <div className={classes.module}>
          <PercentChange
            value={residentSubmissions.value}
            label="Resident submissions"
            percentChange={residentSubmissions.percentChange ?? 0}
            isLoading={isLoading}
          />
        </div>
        {/* Completed work orders */}
        <div className={classes.module}>
          <div
            className={classes.clickable}
            onClick={() => history.push('/workorders/list/search?statuses=COMPLETED')}
          >
            <PercentChange
              value={completedWorkOrders.value}
              label="Completed work orders"
              percentChange={completedWorkOrders.percentChange ?? 0}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
      <div className={classes.row}>
        <div className={classes.module}>
          <div
            className={clsx(classes.card, classes.clickable)}
            onClick={() => history.push('/workorders/list/search?statuses=DENIED')}
          >
            <div>
              <h2 className={`${classes.primaryText} ${contentClass}`}>{deniedWorkOrders.value}</h2>
              <p className={classes.secondaryText} style={{ width: '90%' }}>
                {deniedWorkOrders.label}
              </p>
            </div>
          </div>
        </div>
        <div className={classes.module}>
          <div
            className={clsx(classes.card, classes.clickable)}
            onClick={() => history.push('/workorders/list/search?statuses=CANCELLED')}
          >
            <div>
              <h2 className={`${classes.primaryText} ${contentClass}`}>
                {canceledWorkOrders.value}
              </h2>
              <p className={classes.secondaryText}>{canceledWorkOrders.label}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Residents engaged this month */}
      <div style={{ marginTop: '20px' }}>
        <div className={classes.card}>
          <div>
            <p className={classes.secondaryText} style={{ marginBottom: '5px' }}>
              {residentsEngaged.label}
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'baseline',
              }}
              className={contentClass}
            >
              <h2 className={classes.primaryText}>{residentsEngaged.value} reporters</h2>
              <IonIcon icon={isPositive ? trendingUp : trendingDown} className={classes.icon} />
              <p className={clsx(classes.secondaryText, classes.percentText)}>
                {Math.abs(residentsEngaged.percentChange ?? 0)}%
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PerformanceMetrics;
